import { useCallback, useEffect, useRef } from 'react';
import { loadPendingSocialSignup } from '@/utils/pendingSocialSignup';

/**
 * Whether the visitor was signed in when auth first resolved: null until then, latched after.
 * A Keep-it Apple/Google redirect lands back signed in but is still finishing a public run, so it counts as signed out.
 */
export function useArrivedSignedIn(signedIn: boolean, isAuthLoading: boolean): boolean | null {
  const arrived = useRef<boolean | null>(null);
  if (!isAuthLoading && arrived.current === null) {
    arrived.current = signedIn && !loadPendingSocialSignup();
  }
  return arrived.current;
}

/**
 * On `/`, where Landing redirects a signed-in arrival, the funnel counts only visitors who arrived signed out.
 * `gate(fire)` fires now when that is known, queues while auth is unresolved, and drops for a signed-in arrival.
 * Latched, so a visitor who signs up mid-run keeps reporting. Disabled (fires immediately) everywhere else.
 */
export function usePublicFunnelGate(
  enabled: boolean,
  signedIn: boolean,
  isAuthLoading: boolean
): (fire: () => void) => void {
  const arrivedSignedIn = useArrivedSignedIn(signedIn, isAuthLoading);
  const decided = useRef(arrivedSignedIn);
  decided.current = arrivedSignedIn;
  const pending = useRef<Array<() => void>>([]);

  useEffect(() => {
    if (arrivedSignedIn === null) return;
    const queued = pending.current;
    pending.current = [];
    if (!arrivedSignedIn) queued.forEach((fire) => fire());
  }, [arrivedSignedIn]);

  return useCallback((fire: () => void) => {
    if (!enabled || decided.current === false) fire();
    else if (decided.current === null) pending.current.push(fire);
  }, [enabled]);
}
